// File Metadata:
// Filename: widgetApi.js
// Version: 1.02
// Created: 2025-01-16T20:00:00Z
// Last Modified: 2025-01-16T20:00:00Z
// Authors: Dieuwer Descheemaecker, AI Assistant
// Description: RTK Query API service for widget management and data

import { baseApi } from '../../../store/api/baseApi';

export const widgetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get available widgets
    getAvailableWidgets: builder.query({
      query: () => '/widgets/available',
      providesTags: ['Widget'],
    }),

    // Get user widget preferences
    getUserWidgetPreferences: builder.query({
      query: () => '/widgets/preferences',
      providesTags: ['WidgetPreferences'],
    }),

    // Update user widget preferences
    updateUserWidgetPreferences: builder.mutation({
      query: (preferences) => ({
        url: '/widgets/preferences',
        method: 'PUT',
        body: preferences,
      }),
      invalidatesTags: ['WidgetPreferences'],
    }),

    // Toggle specific widget
    toggleWidget: builder.mutation({
      query: ({ widgetId, enabled }) => ({
        url: `/widgets/preferences/${widgetId}/toggle`,
        method: 'PATCH',
        body: { enabled },
      }),
      // Instead of invalidating tags, manually update the cache with the returned data
      onQueryStarted: async ({ widgetId, enabled }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          // Update the preferences cache with the complete updated preferences
          // Make sure we maintain the same response structure as getUserWidgetPreferences
          dispatch(
            widgetApi.util.updateQueryData('getUserWidgetPreferences', undefined, (draft) => {
              // The backend response should be { success: true, data: preferences }
              // So we need to maintain the same structure
              return {
                success: true,
                data: data.data
              };
            })
          );
        } catch {
          // If the update fails, the cache will remain unchanged
        }
      },
    }),

    // Update widget configuration
    updateWidgetConfig: builder.mutation({
      query: ({ widgetId, config }) => ({
        url: `/widgets/preferences/${widgetId}/config`,
        method: 'PATCH',
        body: { config },
      }),
      invalidatesTags: ['WidgetPreferences', 'WidgetData'],
    }),

    // Widget Data Endpoints
    getQuickStatsData: builder.query({
      query: () => '/widgets/data/quick-stats',
      providesTags: ['WidgetData'],
    }),

    getRecentChoresData: builder.query({
      query: () => '/widgets/data/recent-chores',
      providesTags: ['WidgetData'],
    }),

    getUpcomingTasksData: builder.query({
      query: () => '/widgets/data/upcoming-tasks',
      providesTags: ['WidgetData'],
    }),

    getFamilyLeaderboardData: builder.query({
      query: () => '/widgets/data/family-leaderboard',
      providesTags: ['WidgetData'],
    }),

    getRecentChatData: builder.query({
      query: () => '/widgets/data/recent-chat',
      providesTags: ['WidgetData'],
    }),

    getPointsSummaryData: builder.query({
      query: () => '/widgets/data/points-summary',
      providesTags: ['WidgetData'],
    }),

    getChecklistProgressData: builder.query({
      query: () => '/widgets/data/checklist-progress',
      providesTags: ['WidgetData'],
    }),

    getWeatherData: builder.query({
      query: () => '/widgets/data/weather',
      providesTags: ['WidgetData'],
    }),
  }),
});

export const {
  useGetAvailableWidgetsQuery,
  useGetUserWidgetPreferencesQuery,
  useUpdateUserWidgetPreferencesMutation,
  useToggleWidgetMutation,
  useUpdateWidgetConfigMutation,
  useGetQuickStatsDataQuery,
  useGetRecentChoresDataQuery,
  useGetUpcomingTasksDataQuery,
  useGetFamilyLeaderboardDataQuery,
  useGetRecentChatDataQuery,
  useGetPointsSummaryDataQuery,
  useGetChecklistProgressDataQuery,
  useGetWeatherDataQuery,
} = widgetApi;
